import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ExternalLink, Loader2, FileText, Download } from 'lucide-react';
import { patentApi } from '@/services/api';

interface AssignmentsProps {
  applicationNumber: string;
}

interface Assignment {
  reel_frame?: string;
  recorded_date?: string;
  conveyance_text?: string;
  assignors?: string[];
  assignees?: string[];
  page_count?: string;
  correspondent_name?: string;
  correspondent_address?: string[];
}

export function Assignments({ applicationNumber }: AssignmentsProps) {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAssignments = async () => {
      if (!applicationNumber) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Clean the application number - remove slashes and commas
        const cleanedAppNumber = applicationNumber.replace(/[\/,]/g, '');

        // Fetch assignments from the backend
        const response = await patentApi.fetchAssignments(cleanedAppNumber);

        if (response && Array.isArray(response.assignments)) {
          setAssignments(response.assignments);
        } else {
          setAssignments([]);
        }
      } catch (err) {
        console.error('Failed to fetch assignments:', err);
        setError('Failed to load assignment data. Please try again.');
        setAssignments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAssignments();
  }, [applicationNumber]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const getAssignmentPdfUrl = (reelFrame: string | null | undefined) => {
    if (!reelFrame) return null;

    // Parse reel/frame from string like "12345/0678" or "12345-0678"
    const parts = reelFrame.split(/[\/\-]/);
    if (parts.length !== 2) return null;

    const reelNo = parts[0].trim();
    const frameNo = parts[1].trim();

    if (!reelNo || !frameNo) return null;

    return `https://assignmentcenter.uspto.gov/ipas/search/api/v2/public/download/patent/${reelNo}/${frameNo}`;
  };

  const cleanedAppNumber = applicationNumber.replace(/[\/,]/g, '');
  const assignmentUrl = `https://assignmentcenter.uspto.gov/search/patent/abstract?applicationNumber=${cleanedAppNumber}`;

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Patent Assignments</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mr-2 text-blue-600" />
            <span className="text-sm text-gray-500">Loading assignment data...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Patent Assignments</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(assignmentUrl, '_blank')}
              className="gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              View on USPTO
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-sm text-red-600 mb-4">{error}</p>
            <p className="text-xs text-gray-500">
              You can view assignments directly on the USPTO Assignment website.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (assignments.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Patent Assignments</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(assignmentUrl, '_blank')}
              className="gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              View on USPTO
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-sm mb-2">No assignment records found for this application.</p>
            <p className="text-xs">
              This may mean no assignments have been recorded, or the data is not yet available.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Patent Assignments</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(assignmentUrl, '_blank')}
            className="gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            View on USPTO
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {assignments.map((assignment, index) => {
            const pdfUrl = getAssignmentPdfUrl(assignment.reel_frame);

            return (
              <div
                key={index}
                className="p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200 mb-2">
                      {assignment.conveyance_text || 'Assignment'}
                    </Badge>
                    {assignment.recorded_date && (
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Recorded:</span> {formatDate(assignment.recorded_date)}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {assignment.reel_frame && (
                      <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-200">
                        Reel/Frame: {assignment.reel_frame}
                      </Badge>
                    )}
                    {pdfUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(pdfUrl, '_blank')}
                        className="gap-1.5"
                        title="View Assignment Document (PDF)"
                      >
                        <Download className="w-3.5 h-3.5" />
                        View PDF
                      </Button>
                    )}
                  </div>
                </div>

              <div className="space-y-3">
                {/* Assignors */}
                {assignment.assignors && assignment.assignors.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                      Assignor{assignment.assignors.length > 1 ? 's' : ''}
                    </p>
                    <div className="space-y-1">
                      {assignment.assignors.map((assignor, idx) => (
                        <p key={idx} className="text-sm text-gray-900">
                          {assignor}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {/* Assignees */}
                {assignment.assignees && assignment.assignees.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                      Assignee{assignment.assignees.length > 1 ? 's' : ''}
                    </p>
                    <div className="space-y-1">
                      {assignment.assignees.map((assignee, idx) => (
                        <p key={idx} className="text-sm text-gray-900 font-medium">
                          {assignee}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {/* Correspondent */}
                {assignment.correspondent_name && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                      Correspondent
                    </p>
                    <p className="text-sm text-gray-900">{assignment.correspondent_name}</p>
                    {assignment.correspondent_address && assignment.correspondent_address.length > 0 && (
                      <div className="text-sm text-gray-600 mt-1">
                        {assignment.correspondent_address.map((line, idx) => (
                          <p key={idx}>{line}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Page Count */}
                {assignment.page_count && (
                  <p className="text-xs text-gray-500">
                    <span className="font-medium">Pages:</span> {assignment.page_count}
                  </p>
                )}
              </div>
            </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
